import { useQuery, useMutation } from '@tanstack/react-query';
import type { Document, AskResponse } from '../types';

const BASE = '/api';

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!res.ok) throw new Error(`API error ${res.status}: ${await res.text()}`);
  return res.json() as Promise<T>;
}

export function useDocuments(userId: string, source?: string, limit = 200) {
  return useQuery<Document[]>({
    queryKey: ['documents', userId, source, limit],
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (source) params.set('source', source);
      return apiFetch<Document[]>(`/data/${userId}/logs?${params}`);
    },
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useAsk() {
  return useMutation<AskResponse, Error, { query: string; userId?: string; scope?: string; source?: string; limit?: number }>({
    mutationFn: ({ query, userId, scope, source, limit = 10 }) =>
      apiFetch<AskResponse>('/ask', {
        method: 'POST',
        body: JSON.stringify({ query, user_id: userId, scope, source, limit }),
      }),
  });
}

export function useSync(userId: string) {
  return useMutation<{ inserted_or_updated?: number; error?: string }, Error, string>({
    mutationFn: (source) =>
      apiFetch(`/sync/${userId}/${source}`, { method: 'POST' }),
  });
}

export function useHealth() {
  return useQuery<{ status: string; mock_mode: boolean }>({
    queryKey: ['health'],
    queryFn: () => apiFetch('/health'),
    staleTime: 60_000,
  });
}
